import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { AuthError, Session } from "@supabase/supabase-js";
import type { UserRole } from "@/types/team";

interface LoginCredentials {
  email: string;
  password: string;
  /**
   * Cloudflare Turnstile token. Required when Supabase has Captcha
   * Protection enabled (prod). Omit in environments where captcha is
   * off — passing `undefined` is the documented "no captcha" value.
   */
  captchaToken?: string;
}

interface AuthContextValue {
  loading: boolean;
  error: AuthError | null;
  session: Session | null;
  role: UserRole | null;
  /**
   * The workspace from the access token's `app_metadata.tenant_id`, which
   * the custom access-token hook writes into the JWT only — never into
   * the user record, so `session.user.app_metadata` does not carry it.
   */
  tenantId: string | null;
  signIn: (credentials: LoginCredentials) => Promise<Session | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toAuthError(err: unknown): AuthError {
  if (err instanceof AuthError) return err;
  return new AuthError(err instanceof Error ? err.message : String(err));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  /**
   * Which identity the query cache holds data for. `undefined` means
   * nothing has been observed yet this page load — the cache is empty
   * then, and clearing it would only cancel reads already in flight.
   */
  const cachedIdentity = useRef<string | null | undefined>(undefined);

  /**
   * Adopt a session, dropping any cache the previous identity left.
   *
   * Query keys name a resource, not an account — ["tenant"], ["tokens"],
   * ["team-members"], ["invitations"] — so an entry written for one
   * signed-in user is served to the next one on the same tab. Nothing is
   * persisted, so a page load always starts empty; the only way two
   * identities meet is a change within one tab, which is exactly what
   * this sees.
   *
   * Here rather than in `signOut` because sign-out is not the only way
   * across: a token can expire, another tab can sign out (Supabase syncs
   * sessions through localStorage), someone can sign in without an
   * intervening sign-out, and `AcceptInvitation` swaps a pending tenant
   * for a real one via `refreshSession()` without the user id ever
   * changing — hence the tenant in the identity.
   */
  const applySession = useCallback(
    (session: Session | null) => {
      const claims = extractAuthClaimsFromSession(session);
      const identity = claims.userId
        ? `${claims.userId}:${claims.tenantId ?? ""}`
        : null;
      const previous = cachedIdentity.current;
      cachedIdentity.current = identity;
      if (previous !== undefined && previous !== identity) {
        queryClient.clear();
      }
      setSession(session);
      setRole(claims.role);
      setTenantId(claims.tenantId);
    },
    [queryClient],
  );

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        applySession(session);
      })
      .catch((err: unknown) => {
        setError(toAuthError(err));
      })
      .finally(() => {
        setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    return () => subscription.unsubscribe();
  }, [applySession]);

  const signIn = useCallback(
    async ({
      email,
      password,
      captchaToken,
    }: LoginCredentials): Promise<Session | null> => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: signInError } =
          await supabase.auth.signInWithPassword({
            email,
            password,
            options: { captchaToken },
          });

        if (signInError) {
          setError(signInError);
          return null;
        }

        return data.session;
      } catch (err) {
        setError(toAuthError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const signOut = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        setError(signOutError);
      }
    } catch (err) {
      setError(err as AuthError);
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ loading, error, session, role, tenantId, signIn, signOut }),
    [loading, error, session, role, tenantId, signIn, signOut],
  );

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

interface AuthClaims {
  role: UserRole | null;
  userId: string | null;
  tenantId: string | null;
  roles: UserRole[];
}

const EMPTY_CLAIMS: AuthClaims = {
  role: null,
  userId: null,
  tenantId: null,
  roles: [],
};

// JWT payload is base64url, not base64. atob() requires base64 — payloads
// containing url-safe chars (`-`, `_`) silently fail without normalisation.
function decodeBase64Url(input: string): string {
  const pad = input.length % 4 === 0 ? "" : "=".repeat(4 - (input.length % 4));
  const base64 = (input + pad).replace(/-/g, "+").replace(/_/g, "/");
  return atob(base64);
}

/**
 * Decodes the Supabase access token once and returns subject + custom
 * claims used by both the auth context and browser OTel attribution.
 *
 * Returns null fields and an empty `roles` array when claims are missing
 * or the token is malformed; never throws.
 */
export function extractAuthClaimsFromSession(
  session: Session | null,
): AuthClaims {
  if (!session?.access_token) {
    return { ...EMPTY_CLAIMS };
  }

  try {
    // JWT format: header.payload.signature
    const parts = session.access_token.split(".");
    if (parts.length !== 3) {
      return { ...EMPTY_CLAIMS };
    }

    // Decode base64url payload
    const payload = JSON.parse(decodeBase64Url(parts[1]));

    if (typeof payload !== "object" || payload === null) {
      console.error("Invalid JWT payload structure");
      return { ...EMPTY_CLAIMS };
    }

    const userId =
      typeof payload.sub === "string" && payload.sub.length > 0
        ? payload.sub
        : null;

    let role: UserRole | null = null;
    let tenantId: string | null = null;
    if (
      typeof payload.app_metadata === "object" &&
      payload.app_metadata !== null
    ) {
      if (typeof payload.app_metadata.role === "string") {
        role = payload.app_metadata.role as UserRole;
      }
      if (
        typeof payload.app_metadata.tenant_id === "string" &&
        payload.app_metadata.tenant_id.length > 0
      ) {
        tenantId = payload.app_metadata.tenant_id;
      }
    }

    return {
      role,
      userId,
      tenantId,
      roles: role ? [role] : [],
    };
  } catch (error) {
    console.error("Failed to extract claims from JWT:", error);
    return { ...EMPTY_CLAIMS };
  }
}
